import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import useCustomCompareEffect from 'common/hooks/useCustomCompareEffect.ts';
import {
  getCommentCountsByVersions,
  getCommentCountsFulfilled,
  isCommentCountValid,
  setCommentPanelOpened,
  getComments,
} from 'features/comments/commentsSlice';
import { getCommentCounts } from 'features/comments/commentService';
import useMobile from './useMobile';
import { isInlineCommentingEnabled } from '../helpers/featureFlags';
import { structuredAnalysisTypes } from '../constants/StructuredAnalysisTypes';
import { getIsThirdPartyMember } from '../redux/security/loggedInUser/loggedInUserSlice';

function useVersionToggle(isMounted, draftVersionData, sharedVersionData) {
  const autoOpenedDraftRef = useRef();
  const autoOpenedSharedRef = useRef();
  const dispatch = useDispatch();
  const commentCounts = useSelector(getCommentCountsByVersions);
  const commentCountsFulfilled = useSelector(getCommentCountsFulfilled);
  const isCountValid = useSelector(isCommentCountValid);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const comments = useSelector(getComments);
  const { projectId, entityType, entityId, isEntitySharedWithClient } =
    comments;

  const isMobile = useMobile();
  useCustomCompareEffect(
    () => {
      const isProjectOverview =
        draftVersionData === undefined && sharedVersionData === undefined;
      const { versionNumber: draftVersion = -1 } = draftVersionData || {};
      const { versionNumber: sharedVersion = -1 } = sharedVersionData || {};
      function fetchData() {
        dispatch(
          getCommentCounts({
            projectId,
            entityType,
            entityId,
            draftVersion,
            sharedVersion,
          })
        );
      }

      // This will prevent fetching comment counts more than once
      const isStructuredAnalysis = Object.values(
        structuredAnalysisTypes
      ).includes(entityType);

      const skipFetchData =
        isStructuredAnalysis && entityId === 1 && isThirdPartyMember;

      if (
        projectId &&
        entityType &&
        entityId &&
        !isInlineCommentingEnabled &&
        !skipFetchData &&
        (isProjectOverview || !(draftVersion === -1 && sharedVersion === -1)) &&
        (draftVersion || sharedVersion)
      ) {
        fetchData();
      }
    },
    {
      entityType,
      entityId,
      projectId,
      isCountValid,
      draftVersionData,
      sharedVersionData,
    },
    (prevDeps, currentDeps) => {
      if (
        prevDeps.entityType !== currentDeps.entityType ||
        prevDeps.entityId !== currentDeps.entityId ||
        prevDeps.projectId !== currentDeps.projectId ||
        prevDeps.isCountValid !== currentDeps.isCountValid ||
        !isEqual(prevDeps.draftVersionData, currentDeps.draftVersionData) ||
        !isEqual(prevDeps.sharedVersionData, currentDeps.sharedVersionData)
      ) {
        return false;
      }
      return true;
    }
  );

  const openPanel = useCallback(() => {
    dispatch(setCommentPanelOpened(true));
  }, [dispatch]);

  const autoOpenCommentPanel = useCallback(
    (autoOpenRef, autoCloseRef, versionData) => {
      if (autoOpenRef.current) {
        return;
      }

      autoCloseRef.current = false;

      const { commentCount = 0 } = versionData || {};

      if (commentCount === 0) {
        return;
      }

      if (isMobile) {
        return;
      }

      autoOpenRef.current = true;

      openPanel();
    },
    [openPanel, isMobile]
  );

  useEffect(() => {
    if (!commentCountsFulfilled) {
      return;
    }

    if (isEntitySharedWithClient) {
      const { sharedVersion } = commentCounts || {};

      autoOpenCommentPanel(
        autoOpenedSharedRef,
        autoOpenedDraftRef,
        sharedVersion
      );
    } else {
      const { draftVersion } = commentCounts || {};

      autoOpenCommentPanel(
        autoOpenedDraftRef,
        autoOpenedSharedRef,
        draftVersion
      );
    }
  }, [
    autoOpenCommentPanel,
    commentCounts,
    isEntitySharedWithClient,
    isMounted,
    commentCountsFulfilled,
  ]);

  return {
    isEntitySharedWithClient,
    commentCounts,
  };
}

export default useVersionToggle;
